/**
 * Custom renderRichText replacement
 *
 * Replaces gatsby-source-contentful/rich-text's renderRichText function
 * with our lightweight custom renderer.
 *
 * API-compatible drop-in replacement.
 */

import { ReactNode } from "react"

import {
  type RenderOptions,
  renderToReact,
  type RichTextDocument,
} from "./customRichTextRenderer"

interface RenderRichTextData {
  raw?: null | string
  references?: any[] | null
}

/**
 * Render Contentful rich text to React components
 *
 * @param data - Rich text data from Gatsby GraphQL (with raw field)
 * @param options - Render options with custom node renderers
 * @returns React node tree
 */
export function renderRichText(
  data: null | RenderRichTextData | RichTextDocument | undefined,
  options?: RenderOptions
): ReactNode {
  if (!data) return null

  let doc: null | RichTextDocument = null
  let references: any[] | null = null

  // Handle Gatsby's { raw, references } wrapper format
  if ("raw" in data && data.raw) {
    try {
      doc = typeof data.raw === "string" ? JSON.parse(data.raw) : data.raw
      references = data.references || null
    } catch (e) {
      console.error("Failed to parse rich text raw data:", e)
      return null
    }
  }
  // Handle direct document
  else if ("nodeType" in data && data.nodeType === "document") {
    doc = data as RichTextDocument
  }

  if (!doc) return null

  return renderToReact(doc, options, references)
}
